import { useState, useEffect } from "react";
import styled from "styled-components";
import Colors from '../../ui/global/Colors';
import Fonts from '../../ui/global/Fonts';
import Logo from "../../ui/components/Logo";
import PriceOptions from "./PriceOptions";
import CreditsGrid from "./CreditsGrid";
import Usage from "./Usage";
import { useCreditsContext } from "./CreditsContext";
import { CREDITS_CONSTANTS } from "../../backend/useCredits";
import { useUser } from "../../backend/useUser";
import { useNavigate, useLocation } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { useConfig } from "../../utils/config";
import {
  redirectToSignup as redirectToSignupHelper,
  onClickHobby as onClickHobbyHelper,
  onClickCreator as onClickCreatorHelper,
  onClickUltimate as onClickUltimateHelper,
  handleCreatorPlanChange as handleCreatorPlanChangeHelper,
  getCreatorPrice as getCreatorPriceHelper,
  getLoggedInButtonText,
} from "./priceHelpers";

const Root = styled.div<{ $colorMode: 'light' | 'dark' }>`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100svh;
  width: 100%;
  max-width: 100%;
  background-color: ${props => props.$colorMode === 'light' ? Colors.background : Colors.black};
  padding: 10px;
  gap: 40px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 40px 0;
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 36px;
  gap: 8px;
`;

const Title = styled.h1<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 48px;
  color: ${props => props.$colorMode === 'light' ? Colors.black : Colors.white};
  text-align: center;
  margin: 0;
  margin-top: 8px;
`;

const Subtitle = styled.p<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 18px;
  color: ${props => props.$colorMode === 'light' ? Colors.grey6 : Colors.grey4};
  text-align: center;
  text-wrap: balance;
  margin: 0 0 10px 0;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  max-width: 1200px;
  
  @media (max-width: 1000px) {
    grid-template-columns: 1fr;
    max-width: 100%;
    padding: 0 10px;
  }
`;

interface PricingProps {
  colorMode?: 'light' | 'dark';
}

interface CreditItem {
  type: string;
  item: string;
  value: number;
  unit: string;
}

const Pricing = ({ colorMode = 'dark' }: PricingProps) => {
  const config = useConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription, isLoading } = useCreditsContext();
  const { user } = useUser();
  const [selectedCreatorPlan, setSelectedCreatorPlan] = useState(`${CREDITS_CONSTANTS.CREDIT_LIMITS.PAID_CREATOR_LIMIT}`);
  const isMobile = useMediaQuery('(max-width: 1000px)');
  
  const creditItemsOG: CreditItem[] = [
    { type: 'Game', item: 'Create new game', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_GAME_CREATE, unit: 'credits' },
    { type: 'Game', item: 'Edit existing game', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_GAME_EDIT, unit: 'credit' },
    { type: '2D Art', item: 'Generate image', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_2D, unit: 'credits' },
    { type: '2D Art', item: 'Remove background', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_BG_REMOVE, unit: 'credits' },
    { type: '3D Art', item: 'Generate textured model', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_3D, unit: 'credits' },
  ];

  const creditItemsNew: CreditItem[] = [
    { type: 'Game', item: 'Create new game', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_GAME_CREATE, unit: 'credits' },
    { type: 'Game', item: 'Edit existing game', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_GAME_EDIT, unit: 'credit' },
    { type: 'Art', item: '2D asset', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_2D, unit: 'credits' },
    { type: 'Art', item: '3D model + texture', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_ART_3D, unit: 'credits' },
    { type: 'Art', item: 'Remove background', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_BG_REMOVE, unit: 'credits' },
    { type: 'Animation', item: 'Create animation', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_VIDEO, unit: 'credits' },
    { type: 'Animation', item: 'Remove background', value: CREDITS_CONSTANTS.CREDIT_COSTS.COST_VIDEO_BG_REMOVE, unit: 'credits' },
  ];

  const creditItems = (config.ENABLE_ANIMATION && config.ANIMATION_SHOW_NAV_OPTION) ? creditItemsNew : creditItemsOG;

  // useEffect(() => {
  //   console.log('PRICING user:', user);
  // }, [user]);

  // useEffect(() => {
  //   console.log('PRICING subscription:', subscription);
  // }, [subscription]);

  // useEffect(() => {
  //   console.log('PRICING location.pathname:', location.pathname);
  // }, [location.pathname]);

  // useEffect(() => {
  //   console.log('PRICING location.state:', location.state);
  // }, [location.state]);

  // Wrapper functions that call the helper functions with the correct parameters
  const redirectToSignup = () => redirectToSignupHelper(navigate, location);
  const onClickHobby = () => onClickHobbyHelper(user, navigate, location, subscription, config.ENABLE_FREE_TRIAL);
  const onClickCreator = () => onClickCreatorHelper(user, navigate, location, subscription, config.ENABLE_FREE_TRIAL);
  const onClickUltimate = () => onClickUltimateHelper(user, navigate, location);
  const handleCreatorPlanChange = (event: any) => handleCreatorPlanChangeHelper(event, setSelectedCreatorPlan);
  const getCreatorPrice = () => getCreatorPriceHelper(selectedCreatorPlan);

  const renderFreePriceOption = () => (
    <PriceOptions
      planName="TRIAL"
      price="FREE"
      priceSubText=""
      checklistItems={[
        "Try creating games and art",
        "Daily and monthly AI limits",
        "Play community games",
        // "Free one-time trial gift of 15 credits",
        // "5 free credits per day",
        // "Up to a maximum of 25 free credits per month",
      ]}
      buttonText={!user && !isLoading ? "Get Started" : null}
      style={!user || isLoading ? "regular" : "ghost"}
      colorMode={colorMode}
      disabled={user ? true : false}
      onClick={!user && !isLoading ? redirectToSignup : undefined}
    />
  );

  // If subscription data is still loading, show the pricing options with ghost styling
  return (
    <Root $colorMode={colorMode}>
      <Section>
        <HeaderContainer>
          <Logo size={36} colorMode={colorMode} text={null} />
          <Title $colorMode={colorMode}>Pricing</Title>
          <Subtitle $colorMode={colorMode}>
            Start for free. Upgrade to get the credits and features you need.
          </Subtitle>
        </HeaderContainer>
        <PricingGrid>
          {!isMobile && renderFreePriceOption()} {/* Render FREE at the top only if NOT mobile */}
          <PriceOptions
            planName="HOBBY"
            price="$9.99"
            priceSubText="/ month"
            checklistItems={[
              `Go beyond daily limits with ${CREDITS_CONSTANTS.CREDIT_LIMITS.PAID_HOBBY_LIMIT} monthly AI credits`,
              "Game creation & editing",
              "2D & 3D art generation",
              ...((config.ENABLE_ANIMATION && config.ANIMATION_SHOW_NAV_OPTION) ? ["Animations & sprite sheets"] : []),
              ...(config.GITHUB_SYNC ? ["Sync code to GitHub"] : []),
            ]}
            buttonText={!user || isLoading ? "Get Started" : getLoggedInButtonText(subscription, 'hobby')}
            onClick={onClickHobby}
            style={!user || isLoading ? "primary" : subscription === 'hobby' ? "ghost" : subscription === 'creator' || subscription === 'ultimate' ? "ghost" : "primary"}
            colorMode={colorMode}
          />
          <PriceOptions
            planName="CREATOR"
            price={getCreatorPrice()}
            priceSubText="/ month"
            checklistItems={[
              "Everything in hobby, plus:",
              `${selectedCreatorPlan} monthly AI credits`,
              // "Premium game hosting",
              // "Private projects: disable remix, release when ready",
              // "Export full source code",
              "Priority support",
            ]}
            buttonText={!user || isLoading ? "Get Started" : getLoggedInButtonText(subscription, 'creator')}
            onClick={onClickCreator}
            style={!user || isLoading ? "regular" : subscription === 'creator' ? "regular" : subscription === 'hobby' ? "primary" : "regular"}
            colorMode={colorMode}
            // dropdown={{
            //   show: true,
            //   options: [
            //     "25x monthly AI credits", 
            //     "35x monthly AI credits", 
            //     "45x monthly AI credits", 
            //     "55x monthly AI credits"
            //   ],
            //   initialValue: "25x monthly AI credits",
            //   placeholder: "Select credits multiplier",
            //   position: 1,
            //   onChange: handleCreatorPlanChange
            // }}
          />
          {/* <PriceOptions
            planName="ULTRA"
            price="$49.99"
            priceSubText="/ month"
            checklistItems={[
              "25x monthly AI credits",
              "Game creation & editing",
              "2D & 3D art generation",
              "Private games",
              "Priority support",
            ]}
            buttonText={!user || isLoading ? "Get Started" : (subscription === 'ultimate' ? "Manage Plan" : "Upgrade")}
            onClick={onClickUltimate}
            style={!user || isLoading ? "ghost" : subscription === 'ultimate' ? "ghost" : subscription === 'pro' ? "primary" : "regular"}
            colorMode={colorMode}
          /> */}
          {(isMobile && !user) && renderFreePriceOption()} {/* Render FREE at the bottom only if IS mobile AND user is NOT logged in */}
        </PricingGrid>
      </Section>

      {user && (
        <Section>
          <HeaderContainer>
            <Title $colorMode={colorMode}>Usage</Title>
            <Subtitle $colorMode={colorMode}>
              Your playmix.ai usage this month.
            </Subtitle>
          </HeaderContainer>
          <Usage colorMode={colorMode} />
        </Section>
      )}

      <Section>
        <HeaderContainer>
          <Title $colorMode={colorMode}>Credits</Title>
          <Subtitle $colorMode={colorMode}>
            The amount of credits each AI request costs.
          </Subtitle>
        </HeaderContainer>
        <CreditsGrid creditItems={creditItems} colorMode={colorMode} />
      </Section>
    </Root>
  );
};

export default Pricing;